import { useState } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { useFetch, inr, fmtDate, fmtDT } from "../lib/api";
import { useApp } from "../lib/ctx";
import { Kpi, Loader, Card, Bar, StatusBadge, EmptyState, PageHeader } from "../components/common/kit";

const HEALTH = [["on_track", "On Track", "#22A447"], ["at_risk", "At Risk", "#FF5A00"], ["delayed", "Delayed", "#FF2D00"]];

function KpiRow({ k }) {
  const cards = [["total_leads", "Total Leads", false], ["active_projects", "Active Projects", false], ["contracted_value", "Contracted Value", true],
    ["invoiced", "Invoices Issued", true], ["collections", "Collections", true], ["outstanding", "Outstanding", true]].filter(([key]) => k[key]);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      {cards.map(([key, label, money], i) => (
        <div key={key} className="fade-in" style={{ animationDelay: `${i * 50}ms` }}>
          <Kpi label={label} value={money ? inr(k[key].value, true) : k[key].value} to={k[key].link} testid={`kpi-${key}`} title={k[key].definition}
            tone={key === "outstanding" ? "text-[#FF2D00]" : "text-[#0D131B]"} hint={<span className="line-clamp-3">{k[key].definition}</span>} />
        </div>
      ))}
    </div>
  );
}

function HealthCard({ health, projects }) {
  const total = projects.length;
  const data = HEALTH.map(([k, l, c]) => ({ name: l, value: health[k] || 0, c }));
  return (
    <Card title="Project Progress" testid="project-health-card" action={<Link to="/projects" className="btn-ghost-o">View all</Link>}>
      {total === 0 ? <EmptyState title="No projects yet" text="Projects are created automatically when an agreement is signed." /> : (
        <div className="flex items-center gap-6">
          <div className="h-36 w-36 relative">
            <ResponsiveContainer><PieChart><Pie data={data} dataKey="value" innerRadius={48} outerRadius={64} paddingAngle={2} stroke="none">
              {data.map((d) => <Cell key={d.name} fill={d.c} />)}</Pie></PieChart></ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center"><p className="text-2xl font-bold">{total}</p><p className="text-[10px] text-neutral-500">projects</p></div>
          </div>
          <div className="space-y-2 flex-1">
            {data.map((d) => <div key={d.name} className="flex items-center justify-between text-sm"><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: d.c }} />{d.name}</span><b>{d.value}</b></div>)}
          </div>
        </div>
      )}
      <div className="mt-4 space-y-3">
        {projects.slice(0, 4).map((p) => (
          <Link to={`/projects/${p.id}`} key={p.id} className="block" data-testid={`dash-project-${p.id}`}>
            <div className="flex justify-between text-sm mb-1"><span className="font-medium truncate">{p.name}</span><span>{Math.round(p.progress || 0)}%</span></div>
            <Bar value={p.progress} />
          </Link>
        ))}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { me } = useApp();
  const [period, setPeriod] = useState("year");
  const { data, loading } = useFetch(`/dashboard?period=${period}`, [period]);
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  return (
    <div>
      <PageHeader title={`${greet}, ${me?.name?.split(" ")[0] || ""}`} subtitle="Here's what's happening across your business today."
        actions={<select data-testid="dashboard-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="h-10 rounded-xl border px-3 text-sm bg-white">
          <option value="month">This month</option><option value="quarter">This quarter</option><option value="year">This year</option><option value="all">All time</option></select>} />
      {loading || !data ? <Loader /> : (
        <div className="space-y-6">
          <KpiRow k={data.kpis} />
          {(data.kpis.total_leads?.value ?? 0) === 0 && data.projects.length === 0 && data.kpis.total_leads && (
            <div className="card-c p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid="first-run-banner" style={{ background: "linear-gradient(90deg,#FFF3EC,#fff)" }}>
              <div><p className="h3">Welcome to ConstructONS</p><p className="text-sm text-neutral-600">No business records yet. Start by capturing your first lead — the dashboard fills in from real data as you work.</p></div>
              <Link to="/crm" className="btn-grad" data-testid="first-lead-btn">Add your first lead</Link>
            </div>
          )}
          <div className="grid lg:grid-cols-3 gap-6">
            <HealthCard health={data.health} projects={data.projects} />
            <Card title="Today's Tasks" testid="todays-tasks-card" action={<Link to="/tasks" className="btn-ghost-o">All tasks</Link>}>
              {data.tasks.length === 0 ? <p className="text-sm text-neutral-500">No tasks due today or overdue.</p> : data.tasks.map((t) => (
                <div key={t.id} className="flex items-start gap-3 py-2.5 border-b border-neutral-100 last:border-0">
                  <Clock className={`h-4 w-4 mt-0.5 ${t.due_date < new Date().toISOString().slice(0, 10) ? "text-[#FF2D00]" : "text-[#FF5A00]"}`} />
                  <div className="flex-1"><p className="text-sm">{t.title}</p><p className="text-xs text-neutral-500">Due {fmtDate(t.due_date)}</p></div>
                  <StatusBadge value={t.priority} />
                </div>
              ))}
            </Card>
            <Card title="Recent Activity" testid="recent-activity-card">
              {data.activity.length === 0 ? <p className="text-sm text-neutral-500">Activity appears as your team works.</p> : data.activity.map((a) => (
                <div key={a.id} className="flex gap-3 py-2 border-b border-neutral-100 last:border-0">
                  <span className="h-2 w-2 rounded-full mt-1.5" style={{ background: "var(--grad-primary)" }} />
                  <div><p className="text-sm">{a.text}</p><p className="text-[11px] text-neutral-400">{a.actor_name} · {fmtDT(a.at)}</p></div>
                </div>
              ))}
            </Card>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <Card title="Pending Approvals" testid="pending-approvals-card" action={<Link to="/approvals" className="btn-ghost-o">Open queue</Link>}>
              {data.approvals.length === 0 ? <p className="text-sm text-neutral-500 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Nothing waiting.</p> :
                data.approvals.map((a) => <div key={a.id} className="py-2 border-b border-neutral-100 last:border-0"><p className="text-sm">{a.title}</p><p className="text-xs text-neutral-500">{a.requested_by_name} · {inr(a.amount)}</p></div>)}
            </Card>
            <Card title="Upcoming Milestones" testid="milestones-card">
              {data.milestones.length === 0 ? <p className="text-sm text-neutral-500">No upcoming milestones with due dates.</p> : data.milestones.map((m) => (
                <Link to={`/projects/${m.project_id}`} key={m.id} className="flex justify-between py-2 border-b border-neutral-100 last:border-0 text-sm">
                  <span>{m.name}<span className="block text-xs text-neutral-500">{m.project_name}</span></span><span className="text-xs">{fmtDate(m.due_date)}</span></Link>
              ))}
            </Card>
            <Card title="Material & Quality Alerts" testid="alerts-card">
              {data.alerts.low_stock.length + data.alerts.corrective_actions.length === 0 ? <p className="text-sm text-neutral-500">No open alerts.</p> : <>
                {data.alerts.low_stock.map((m) => <p key={m.id} className="text-sm py-1.5 flex gap-2"><AlertTriangle className="h-4 w-4 text-[#FF5A00]" />{m.name}: stock {m.stock} (reorder at {m.reorder_level})</p>)}
                {data.alerts.corrective_actions.map((c) => <p key={c.id} className="text-sm py-1.5 flex gap-2"><AlertTriangle className="h-4 w-4 text-[#FF2D00]" />{c.title}</p>)}
              </>}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
